import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const PUSH_SW_PATH = '/push-sw.js';
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

/**
 * Capability detection. Web Push depends on three browser APIs + our VAPID
 * key being configured at build time + (on iOS) the site being installed
 * as a PWA. Each failure reason maps to a specific empty-state message.
 */
export type PushSupport =
  | { supported: true }
  | {
      supported: false;
      reason:
        | 'no_service_worker'
        | 'no_push_manager'
        | 'no_notifications'
        | 'no_vapid_key'
        | 'ios_needs_pwa_install';
    };

export type PushPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export type UsePushSubscriptionResult = {
  support: PushSupport;
  permission: PushPermissionState;
  isSubscribed: boolean;
  isPending: boolean;
  error: string | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
};

function detectSupport(): PushSupport {
  // iOS-specific gate: on iPhone/iPad, the Web Push API is only exposed
  // when the site is installed as a PWA and opened from the home screen
  // (iOS 16.4+ Safari only — Chrome on iOS uses Apple's WebKit and never
  // exposes Push in browser-tab mode). Surface this BEFORE the generic
  // capability checks so iOS users see install-PWA guidance rather than
  // an unhelpful "not supported" message.
  if (typeof navigator !== 'undefined' && typeof window !== 'undefined') {
    const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const standaloneNav = (window.navigator as { standalone?: boolean }).standalone === true;
    const standaloneMq =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(display-mode: standalone)').matches
        : false;
    if (isIos && !standaloneNav && !standaloneMq) {
      return { supported: false, reason: 'ios_needs_pwa_install' };
    }
  }

  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return { supported: false, reason: 'no_service_worker' };
  }
  if (typeof window === 'undefined' || !('PushManager' in window)) {
    return { supported: false, reason: 'no_push_manager' };
  }
  if (!('Notification' in window)) {
    return { supported: false, reason: 'no_notifications' };
  }
  if (!VAPID_PUBLIC_KEY) {
    return { supported: false, reason: 'no_vapid_key' };
  }
  return { supported: true };
}

/**
 * Web Push expects the applicationServerKey as a raw Uint8Array, not a
 * base64url string. This decode mirrors the inverse of what
 * `scripts/generate-vapid-keys.mjs` produces.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  // Explicit ArrayBuffer (not SharedArrayBuffer) so the result narrows to
  // ArrayBufferView<ArrayBuffer> — required by PushManager.subscribe's
  // applicationServerKey parameter under TS strict mode.
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return view;
}

/**
 * Bidder-side Web Push subscription manager.
 *
 * Returns the current capability/permission/subscription state plus
 * imperative `subscribe()` / `unsubscribe()` actions. The subscribe action
 * walks the full Web Push flow:
 *   1. Ask the browser for Notification permission.
 *   2. Register the service worker at `/push-sw.js`.
 *   3. Call `PushManager.subscribe()` with our VAPID public key.
 *   4. POST the resulting PushSubscription to the `subscribe-to-push` Edge
 *      Function which writes the row in `public.push_subscriptions`.
 *
 * The send-push-notification EF then reads back from that table when fanning
 * out a notification to a user with multiple devices.
 */
export function usePushSubscription(): UsePushSubscriptionResult {
  const [support] = useState<PushSupport>(() => detectSupport());
  const [permission, setPermission] = useState<PushPermissionState>(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  });
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On mount: register the SW if supported, then check whether a
  // PushSubscription already exists for this browser. Don't auto-subscribe;
  // the bidder has to click the enable button.
  useEffect(() => {
    if (!support.supported) return;

    let cancelled = false;

    void (async () => {
      try {
        const reg = await navigator.serviceWorker.register(PUSH_SW_PATH);
        const existing = await reg.pushManager.getSubscription();
        if (!cancelled) setIsSubscribed(existing !== null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to check push subscription');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [support]);

  const subscribe = useCallback(async () => {
    if (!support.supported || !VAPID_PUBLIC_KEY) return;

    setIsPending(true);
    setError(null);

    try {
      // 1. Permission. Chrome/Firefox return the new state synchronously;
      // Safari surfaces a user-facing prompt and returns after the user
      // dismisses it. Either way we read the final state into local state.
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        throw new Error('Notification permission denied');
      }

      // 2. Register the SW + create the PushSubscription.
      const reg = await navigator.serviceWorker.register(PUSH_SW_PATH);
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // 3. POST to subscribe-to-push EF.
      // The EF reads auth from the request JWT, derives user_id + tenant_id,
      // and upserts on endpoint (so re-subscribing the same browser updates
      // last_seen_at rather than creating duplicate rows). Cast follows the
      // pattern used elsewhere in the bidder app (CcPreauthPanel, PhotoManager).
      const { error: efError } = (await supabase.functions.invoke('subscribe-to-push', {
        body: {
          subscription: subscription.toJSON(),
          user_agent: navigator.userAgent,
        },
      })) as { data: unknown; error: { message: string } | null };
      if (efError) throw new Error(efError.message);

      setIsSubscribed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe to push');
    } finally {
      setIsPending(false);
    }
  }, [support]);

  const unsubscribe = useCallback(async () => {
    if (!support.supported) return;

    setIsPending(true);
    setError(null);

    try {
      const reg = await navigator.serviceWorker.getRegistration(PUSH_SW_PATH);
      const subscription = await reg?.pushManager.getSubscription();
      if (subscription) {
        // Tell the backend first so the row gets soft-deleted (disabled_at)
        // before the local subscription dies. If the EF fails we don't kill
        // the local subscription — better to keep receiving pushes than to
        // have the device silently fall off without backend knowing.
        const { error: efError } = (await supabase.functions.invoke('unsubscribe-from-push', {
          body: { endpoint: subscription.endpoint },
        })) as { data: unknown; error: { message: string } | null };
        if (efError) throw new Error(efError.message);

        await subscription.unsubscribe();
      }
      setIsSubscribed(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unsubscribe');
    } finally {
      setIsPending(false);
    }
  }, [support]);

  return {
    support,
    permission,
    isSubscribed,
    isPending,
    error,
    subscribe,
    unsubscribe,
  };
}
